import { cleanupSDKData } from "../RakutenSDK";
import store from "../../../utils/store";
import { setTokenFlag } from "../../../reducers/auth/tokenFlag.reducer";
import { removeWithExpiry } from "../../../context/cachedData";

export const API_URL = import.meta.env.VITE_ENVIRONMENT === "local"
    ? import.meta.env.VITE_API_URL
    : window.location.origin.toString() + "/api";
const application_json = "application/json";

const getConfig = (method, abortSignal): RequestInit => {
  return {
    method, // *GET, POST, PUT, DELETE, etc.
    headers: {
      "Content-Type": application_json,
      Accept: application_json,
      "access-control-allow-origin": "*",
      "Access-Control-Allow-Origin": "*",
    },
    // mode: "cors", // no-cors, *cors, same-origin
    // cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    // credentials: "include", // include, *same-origin, omit
    // redirect: "manual", // manual, *follow, error
    // referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    // referrer: "about:client", // or "" to send no Referer header,
    // or an url from the current origin
    // referrerPolicy: "no-referrer-when-downgrade", // no-referrer, origin, same-origin...
    mode: "cors", // same-origin, no-cors
    credentials: "include", // omit, include
    cache: "default", // no-store, reload, no-cache, force-cache, or only-if-cached
    redirect: "follow", // manual, error
    // integrity: "", // a hash, like "sha256-abcdef1234567890"
    // keepalive: false, // true
    signal: abortSignal, // AbortController to abort request
    // window: window, // null
  };
};
/**
 * Flatten a JSON object to a single level
 */
const flatten = (object, prefix = "") => {
  return Object.keys(object).reduce((carry, key) => {
    const pre = prefix ? prefix + `[${key}]` : "";

    if (Array.isArray(object[key])) {
      carry = object[key].reduce((array, value, index) => {
        array[(pre || key) + `[${index}]`] = value;
        return array;
      }, carry);
    } else if (object[key] && typeof object[key] === "object") {
      Object.assign(carry, flatten(object[key], pre || key));
    } else {
      carry[pre || key] = object[key];
    }

    return carry;
  }, {});
};

const checkResponseType = async <ResponseData>(response: Response) => {
  const contentType = response.headers.get("content-type");
  let modifiedResponse: unknown;
  if (!contentType || !contentType.includes(application_json)) {
    if (contentType.includes("application/pdf")) {
      modifiedResponse = await response.blob();
    }

    const payload = await response.text();
    modifiedResponse = { payload: payload || response.status } as unknown;

    return modifiedResponse as ResponseData;
  }

  return (await response.json()) as ResponseData;
};

const interceptor = async (response) => {
  // TODO Collect all error cases and convert this into a proper class
  const responseJSON = await response.json();

  // code: "Unauthorized"
  // message: "caused by UnauthorizedError: {"exception":"ExpiredJWTTokenException","error_message":"io.jsonwebtoken.ExpiredJwtException: JWT expired at 2021-01-28T14:33:20+0000. Current time: 2021-01-28T14:53
  if (
    // Errors originating in lower level auth are not aligned, getting .error,
    // STAY ON THE PAGE IF THE USER IS NOT FOUND
    responseJSON.code &&
    responseJSON["code"] === "Unauthorized" &&
    ([
      "Authorization info missing",
      "Bad authorization info",
      "caused by UnauthorizedError",
    ].includes(responseJSON.message) ||
      responseJSON.message.match("ExpiredJWTTokenException"))
  ) {
    cleanupSDKData();

    const flagValue = store.getState().auth.tokenFlag.flag;
    if (flagValue !== "expired" &&
      !response.url.includes("/login") &&
      !response.url.includes("/sms/submit")
    ) {
      removeWithExpiry("user_details");
      store.dispatch(setTokenFlag("expired"));
    }
    return Promise.reject(responseJSON);
  }

  //seems the response config after the service worker falls in this case

  if (response.status === 401) {
    const flagValue = store.getState().auth.tokenFlag.flag;
    if (flagValue !== "expired" &&
      !response.url.includes("/login") &&
      !response.url.includes("/sms/submit")
    ) {
      removeWithExpiry("user_details");
      store.dispatch(setTokenFlag("expired"));
    }
  }

  return Promise.reject(responseJSON);
};

const filterDataKeys = (data) => {
  Object.keys(data).forEach((key) => data[key] === undefined && delete data[key]);

  return data;
};

/**
 * Wrap the fetch request, handle the GET / POST data placement
 * @param {string} method defaults to GET
 * @param {string} url defaults to ""
 * @param {*} data  defaults to null
 * @param {*} headers defaults to {}
 * @param {*} abortSignal
 */

export async function requestData<
  ResponseData = any,
  RequestData = Object | string | null,
>(
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  url: string = "",
  data: RequestData = null,
  headers: Object | string = {},
  abortSignal: AbortSignal | undefined = undefined,
): Promise<ResponseData> {
  const targetURL = new URL(API_URL + url);
  let config = getConfig(method, abortSignal);
  if (headers) {
    config.headers = Object.assign(config.headers, headers);
  }

  if (data) {
    // cleanup any undefined parts of the object
    data = filterDataKeys(data);
    if (method !== "GET") {
      config.body = JSON.stringify(data);
    } else {
      for (const pair of Object.entries(flatten(data))) {
        targetURL.searchParams.set(pair[0], String(pair[1]));
      }
    }
  }

  let original_response: Response;
  try {
    original_response = await fetch(targetURL as any, config);
  } catch (error) {
    if (error.name === "AbortError") {
      // Handling error thrown by aborting request
      console.info("abort", error);
      throw error;
    }
  }
  if (!original_response) return Promise.reject({ message: "Server error" });
  const response = original_response.clone();

  if (response.status === 204) return {} as ResponseData;

  if (response.status === 503) return {} as ResponseData;

  if (response.ok) return await checkResponseType<ResponseData>(response);
  else return await interceptor(response);  
}
